import {
  NutritionPageFormulaSection,
  NutritionPageFormulaSectionProps,
} from '@screens/NutritionPage/NutritionPageFormulaSection';

import { StickyNavBar } from '@design-system/StickyNavBar';
import { SearchBarTrigger } from '@/components/shared/SearchBar';
import { getBackgroundImageUrl } from '@utils/nutrition/getImageUrl';
import { NutritionPageFoodsSection } from './NutritionPageFoodsSection';
import { NutritionTipSection } from '@screens/NutritionPage/NutritionTipSection';
import { NutritionPageEducationCarousel } from './NutritionPageEducationCarousel';
import { NutritionTipSectionProps } from '@screens/NutritionPage/NutritionTipSection';
import { NutritionPageFoodsSectionProps } from '@screens/NutritionPage/NutritionPageFoodsSection';
import { NutritionJourneyDeeperSection } from '@screens/NutritionPage/NutritionJourneyDeeperSection';
import { JourneyEducationTypes } from '@utils/JourneyEducationTypes';
import EducationVideoCarousel from '../HomePage/EducationVideoCarousel/EducationVideoCarousel';
import { Render } from '@/components/Helpers/ComponentRender/Render';
import { useRef } from 'react';
import { useScrollToElementOnLoad } from '@/hooks/useScrollToElementOnLoad';
import { MyRecipesSection } from './MyRecipesSection';

export interface NutritionContainerProps {
  headerImageUrl?: string;
  tipsData: NutritionTipSectionProps;
  foodsData: NutritionPageFoodsSectionProps;
  formulasData: NutritionPageFormulaSectionProps;
}

export const NutritionContainer = ({
  foodsData,
  tipsData,
  formulasData,
}: NutritionContainerProps) => {
  const nutritionTipsRef = useRef<HTMLDivElement>(null);

  const NAVBAR_OFFSET = 70;

  useScrollToElementOnLoad({
    targetId: 'nutritionTips',
    targetRef: nutritionTipsRef,
    loadingStates: [
      foodsData.isLoading ?? false,
      formulasData.isLoading ?? false,
      tipsData.isLoading ?? false,
    ],
    scrollOffset: NAVBAR_OFFSET,
  });

  return (
    <div className="relative" data-testid="nutrition-container">
      <StickyNavBar data-testid="nutrition-navbar">
        <div className="page-container !py-0 flex flex-row items-center">
          <span className="text-primary typography-h3 ml-auto pl-11" data-testid="nutrition-navbar-title">
            Nutrition
          </span>

          <SearchBarTrigger className="ml-auto" variant="compact" />
        </div>
      </StickyNavBar>

      <section className="relative" data-testid="nutrition-header-section">
        <div
          className="absolute top-0 left-0 w-full bg-cover bg-no-repeat bg-center z-0"
          style={{
            height: '393px',
            backgroundImage: `
      linear-gradient(to bottom, rgba(244, 244, 245, 0) 10%, rgba(244, 244, 245, 0.9) 90%, #F4F4F5 100%),
      url(${getBackgroundImageUrl('nutrition_background.png')})
    `,
          }}
          data-testid="nutrition-backgroundImage"
        />

        <div className="page-container flex flex-row !py-8 mb-32" data-testid="nutrition-header-content">
          <h1 className="typography-d2 relative" data-testid="nutrition-title">Nutrition</h1>

          <SearchBarTrigger
            responsive
            variant="glass"
            placeholder="Search"
            className="md:max-w-80 w-full ml-auto"
          />
        </div>

        <div className="page-container grid grid-cols-1">
          <NutritionPageEducationCarousel data-testid="nutrition-educationCarousel" />
        </div>

        <div className="page-container grid grid-cols-1">
          <NutritionPageFoodsSection
            foodRanks={foodsData.foodRanks}
            isLoading={foodsData.isLoading}
            isError={foodsData.isError}
            data-testid="nutrition-foods-section"
          />
        </div>
      </section>

      <MyRecipesSection />
      
      <Render when={formulasData && formulasData.formulas && formulasData.formulas.length > 0}>
        <section
          className="relative bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url(${getBackgroundImageUrl('formula_background.png')})`,
          }}
          data-testid="nutrition-formulas-section"
        >
          <div className="page-container grid grid-cols-1 !py-12">
            <NutritionPageFormulaSection
              formulas={formulasData.formulas}
              isLoading={formulasData.isLoading}
              isError={formulasData.isError}
              data-testid="nutrition-formulas-content"
            />
          </div>
        </section>
      </Render>

      <section className="page-container grid grid-cols-1 !pt-12 !pb-24" data-testid="nutrition-tips-section">
        <div ref={nutritionTipsRef}>
          <NutritionTipSection
            tips={tipsData.tips}
            isLoading={tipsData.isLoading}
            isError={tipsData.isError}
            data-testid="nutrition-tips-content"
          />
        </div>

        <div className="mt-6" data-testid="nutrition-educationVideoCarousel">
          <EducationVideoCarousel screen="nutrition" />
        </div>
        <NutritionJourneyDeeperSection
          sectionInfo={JourneyEducationTypes.NUTRITION_FOOTER}
          screen="nutrition"
        />
      </section>
    </div>
  );
};
